// Contracts Service - API endpoints for contracts (hiring codes) management

import { api } from './api';
import { hiringService } from './hiringService';
import type {
  Contract,
  ContractListResponse,

  BulkExpireResponse,
  ContractFilters,
  ContractCreateRequest,
  ContractUpdateRequest,
  ContractAnnex,
  ContractAnnexCreateRequest,
  ContractAnnexUpdateRequest,
  AnnexAttachmentInfo,
  HiringPaymentCreate,
  AdminContractCreateResponse,
  ResendContractEmailResponse,
} from '@/types/contracts';
import type { StripeBillingPortalSession, StripeBillingSummary } from '@/types/stripe';
import { searchTokens, anyFieldContainsAllTokens } from '@/utils/searchText';

/**
 * Normalize hiring code response to Contract format
 */
function normalizeHiringCode(hiringCode: any): Contract {
    // Helper to clean "N/A" values and provide defaults
    const cleanServiceName = (value: any): string => {
      if (!value || value === 'N/A' || value === 'n/a' || value === null || value === undefined || String(value).trim() === '') {
        // Valores por defecto según el tipo de servicio común
        return 'Autorización inicial de Residencia y Trabajo';
      }
      return String(value).trim();
    };
    
    const cleanServiceDescription = (value: any): string => {
      if (!value || value === 'N/A' || value === 'n/a' || value === null || value === undefined || String(value).trim() === '') {
        return 'Tramitación de expediente para obtención de la primera residencia temporal laboral en España';
      }
      return String(value).trim();
    };
    
    return {
      id: String(hiringCode.id || hiringCode.hiring_code),
      hiring_code: hiringCode.hiring_code,
      client_name: hiringCode.client_name || hiringCode.user_name || '',
      client_email: hiringCode.client_email || hiringCode.user_email || '',
      service_name: cleanServiceName(hiringCode.service_name),
      service_description: cleanServiceDescription(hiringCode.service_description),
      amount: hiringCode.amount || 0,
      currency: hiringCode.currency || 'EUR',
      status: (hiringCode.status || 'pending') as Contract['status'],
      kyc_status: hiringCode.kyc_status || null,
      grade: hiringCode.grade,
      payment_type: hiringCode.payment_type,
      expires_at: hiringCode.expires_at || '',
      created_at: hiringCode.created_at || hiringCode.contract_date || '',
      updated_at: hiringCode.updated_at || '',
      short_url: hiringCode.short_url || '',
      client_passport: hiringCode.client_passport,
      client_nie: hiringCode.client_nie,
      client_nationality: hiringCode.client_nationality,
      client_address: hiringCode.client_address,
      client_city: hiringCode.client_city,
      client_province: hiringCode.client_province,
      client_postal_code: hiringCode.client_postal_code,
      contract_date: hiringCode.contract_date,
      contract_accepted: hiringCode.contract_accepted,
      contract_accepted_at: hiringCode.contract_accepted_at,
      contract_pdf_url: hiringCode.contract_pdf_url,
      manual_payment_confirmed: hiringCode.manual_payment_confirmed,
      manual_payment_note: hiringCode.manual_payment_note,
      manual_payment_method: hiringCode.manual_payment_method,
      payment_intent_id: hiringCode.payment_intent_id,
      subscription_id: hiringCode.subscription_id,
      subscription_status: hiringCode.subscription_status,
      first_payment_amount: hiringCode.first_payment_amount,
      contact_id: hiringCode.contact_id,
      contract_type: hiringCode.contract_type,
      linked_group_id: hiringCode.linked_group_id,
      linked_contract_code: hiringCode.linked_contract_code,
      linked_partner_name: hiringCode.linked_partner_name,
      holders: hiringCode.holders || hiringCode.titulars,
      dependents: hiringCode.dependents,
      // Contrato definitivo y mandatos (CRM / hiring_payments / contract_mandates)
      final_contract_url: hiringCode.final_contract_url != null ? String(hiringCode.final_contract_url) : undefined,
      mandate_status: hiringCode.mandate_status ?? undefined,
      mandate_pdf_url: hiringCode.mandate_pdf_url != null ? String(hiringCode.mandate_pdf_url) : undefined,
      mandate_signed_at: hiringCode.mandate_signed_at ?? undefined,
      mandate_codes: hiringCode.mandate_codes ?? undefined,
      mandates: hiringCode.mandates ?? undefined,
      language: hiringCode.language ?? 'es',
    };
}

export const contractsService = {
  /**
   * Get all contracts (hiring codes) with filters and pagination
   * Uses /crm/contracts/ endpoint with JWT authentication
   *
   * El backend puede devolver:
   * - Formato paginado: { items: Contract[], total: number, skip?: number, limit?: number }
   * - Formato legacy: array de contratos (se aplica paginación y total en cliente)
   */
  async getContracts(filters?: ContractFilters): Promise<ContractListResponse> {
    try {
      const skip = filters?.skip || 0;
      const limit = filters?.limit || 20;
      const params: Record<string, string | number> = {
        skip,
        limit,
      };

      if (filters?.status && filters.status !== 'all') {
        params.status_filter = filters.status;
      }
      if (filters?.kyc_status && filters.kyc_status !== 'all') {
        params.kyc_status = filters.kyc_status;
      }
      if (filters?.grade && filters.grade !== 'all') {
        params.grade = filters.grade;
      }
      if (filters?.payment_type && filters.payment_type !== 'all') {
        params.payment_type = filters.payment_type;
      }
      if (filters?.search?.trim()) {
        params.search = filters.search.trim();
      }

      const { data } = await api.get<Contract[] | ContractListResponse>('/crm/contracts/', {
        params,
      });

      // Backend devuelve formato paginado { items, total, skip?, limit? }
      const isPaginatedResponse =
        data &&
        typeof data === 'object' &&
        !Array.isArray(data) &&
        'items' in data &&
        'total' in data;

      if (isPaginatedResponse) {
        const resp = data as ContractListResponse;
        const rawItems = Array.isArray(resp.items) ? resp.items : [];
        const total = typeof resp.total === 'number' ? resp.total : rawItems.length;
        return {
          items: rawItems.map((hc) => normalizeHiringCode(hc)),
          total,
          skip: typeof resp.skip === 'number' ? resp.skip : skip,
          limit: typeof resp.limit === 'number' ? resp.limit : limit,
          stats: resp.stats,
        };
      }

      // Formato legacy: array directo
      let contracts = (Array.isArray(data) ? data : []).map((hc) => normalizeHiringCode(hc));

      // Aplicar filtros solo si el backend no los soportó (respuesta legacy)
      if (filters?.status && filters.status !== 'all') {
        contracts = contracts.filter((c) => c.status === filters.status);
      }
      if (filters?.kyc_status && filters.kyc_status !== 'all') {
        contracts = contracts.filter((c) => c.kyc_status === filters.kyc_status);
      }
      if (filters?.grade && filters.grade !== 'all') {
        contracts = contracts.filter((c) => c.grade === filters.grade);
      }
      if (filters?.payment_type && filters.payment_type !== 'all') {
        contracts = contracts.filter((c) => c.payment_type === filters.payment_type);
      }
      if (filters?.search?.trim()) {
        const tokens = searchTokens(filters.search);
        contracts = contracts.filter((c) => {
          // Todos los campos buscables: titular(es), datos del hiring code y cualquier otro dato del contrato
          const searchableFields: (string | null | undefined)[] = [
            c.hiring_code,
            c.client_name,
            c.client_email,
            c.service_name,
            c.service_description,
            c.client_nie,
            c.client_passport,
            c.client_address,
            c.client_city,
            c.client_province,
            c.client_postal_code,
            c.client_nationality,
            c.linked_partner_name,
            c.linked_contract_code,
            c.contact_id,
            c.notes,
            c.short_url,
            // Titulares (holders): nombre y cualquier dato de cada titular
            ...(c.holders?.flatMap((h) => [
              h.full_name,
              h.email,
              h.phone,
              h.document_id,
              h.nationality,
              h.address,
              h.city,
              h.province,
              h.postal_code,
            ]) ?? []),
            // Dependientes (menores etc.): nombre y relación
            ...(c.dependents?.flatMap((d) => [d.full_name, d.relationship]) ?? []),
          ];
          return anyFieldContainsAllTokens(tokens, ...searchableFields);
        });
      }

      const total = contracts.length;
      const items = contracts.slice(skip, skip + limit);

      return {
        items,
        total,
        skip,
        limit,
      };
    } catch (error: any) {
      console.error('Error fetching contracts:', error);
      // Return empty response on error
      return {
        items: [],
        total: 0,
        skip: filters?.skip || 0,
        limit: filters?.limit || 20,
      };
    }
  },

  /**
   * Get total count of contracts matching filters (sin traer items).
   * Hace GET /crm/contracts/ con skip=0 y limit=0 para que el backend devuelva
   * { items: [], total: N }. En error devuelve -1 para que el listado haga fallback a response.total.
   */
  async getContractsCount(filters?: Omit<ContractFilters, 'skip' | 'limit'>): Promise<number> {
    try {
      const params: Record<string, string | number> = { skip: 0, limit: 0 };
      if (filters?.status && filters.status !== 'all') params.status_filter = filters.status;
      if (filters?.kyc_status && filters.kyc_status !== 'all') params.kyc_status = filters.kyc_status;
      if (filters?.grade && filters.grade !== 'all') params.grade = filters.grade;
      if (filters?.payment_type && filters.payment_type !== 'all') params.payment_type = filters.payment_type;
      if (filters?.search?.trim()) params.search = filters.search.trim();

      const { data } = await api.get<Contract[] | ContractListResponse>('/crm/contracts/', { params });

      const isPaginated =
        data && typeof data === 'object' && !Array.isArray(data) && 'total' in data;
      if (isPaginated && typeof (data as ContractListResponse).total === 'number') {
        return (data as ContractListResponse).total;
      }
      if (Array.isArray(data)) return data.length;
      return 0;
    } catch {
      return -1;
    }
  },

  /**
   * Get contract by hiring code
   * Uses /crm/contracts/{code} endpoint with JWT authentication
   * Falls back to public /hiring/{code} endpoint to get complete data (service_name, service_description)
   */
  async getContract(code: string): Promise<Contract> {
    try {
      // Try admin endpoint first
      const { data: adminData } = await api.get<Contract>(`/crm/contracts/${code}`);
      
      // Get public endpoint data to ensure we have service_name and service_description
      try {
        const publicData = await hiringService.getDetails(code);
        console.log('📋 Datos del endpoint público (raw):', publicData);
        console.log('📋 service_name del público:', publicData.service_name, typeof publicData.service_name);
        console.log('📋 service_description del público:', publicData.service_description, typeof publicData.service_description);
        console.log('📋 Datos del endpoint admin (raw):', adminData);
        console.log('📋 service_name del admin:', adminData.service_name, typeof adminData.service_name);
        console.log('📋 service_description del admin:', adminData.service_description, typeof adminData.service_description);
        
        // Helper to check if value is valid (not empty, not "N/A")
        const isValidValue = (value: any) => {
          if (!value) return false;
          const str = String(value).trim();
          return str !== '' && str !== 'N/A' && str !== 'n/a';
        };
        
        // Merge: use admin data but fill in service info from public endpoint if missing or invalid
        const mergedData = {
          ...adminData,
          service_name: isValidValue(adminData.service_name) 
            ? adminData.service_name 
            : (isValidValue(publicData.service_name) ? publicData.service_name : 'Servicio de Migro'),
          service_description: isValidValue(adminData.service_description)
            ? adminData.service_description
            : (isValidValue(publicData.service_description) ? publicData.service_description : 'Tramitación de expediente administrativo'),
        };
        
        console.log('📋 Datos mergeados finales:', {
          service_name: mergedData.service_name,
          service_description: mergedData.service_description,
        });
        
        return normalizeHiringCode(mergedData);
      } catch {
        // If public endpoint fails, just use admin data
        console.warn('⚠️ No se pudo obtener datos del endpoint público, usando solo datos del admin');
        return normalizeHiringCode(adminData);
      }
    } catch {
      // Fallback to public hiring endpoint
      console.warn('⚠️ Endpoint admin falló, usando endpoint público');
      const data = await hiringService.getDetails(code);
      return normalizeHiringCode(data);
    }
  },

  /**
   * Get contract by hiring code (alias)
   */
  async getContractByCode(code: string): Promise<Contract> {
    return this.getContract(code);
  },

  /**
   * Get contracts for the authenticated client (JWT auth, no admin password)
   * Backend endpoint: GET /client/contracts or /me/contracts
   */
  async getClientContracts(): Promise<Contract[]> {
    try {
      // Intentar endpoint de cliente primero
      const { data } = await api.get<Contract[]>('/client/contracts');
      return (Array.isArray(data) ? data : []).map((hc) => normalizeHiringCode(hc));
    } catch (err: unknown) {
      // Si no existe, intentar con /me/contracts
      try {
        const { data } = await api.get<Contract[]>('/me/contracts');
        return (Array.isArray(data) ? data : []).map((hc) => normalizeHiringCode(hc));
      } catch {
        // Si ambos fallan, retornar array vacío (backend aún no implementado)
        console.warn('⚠️ Endpoints /client/contracts y /me/contracts no disponibles aún');
        return [];
      }
    }
  },

  /**
   * Get Stripe billing summary for a hiring code
   * Uses JWT authentication
   */
  async getStripeBillingSummary(code: string): Promise<StripeBillingSummary> {
    // Intentar endpoint de cliente primero (para usuarios autenticados como clientes)
    try {
      const { data } = await api.get<StripeBillingSummary>(`/clientes/contracts/${code}/stripe/summary`);
      return data;
    } catch {
      // Fallback a endpoint admin si el cliente no existe
      try {
        const { data } = await api.get<StripeBillingSummary>(`/crm/contracts/${code}/stripe/summary`);
        return data;
      } catch {
        throw new Error('No se pudo obtener la información de facturación');
      }
    }
  },

  /**
   * Get Stripe billing summary for a hiring code (client endpoint only)
   * Uses JWT authentication - specifically for client portal
   * Backend endpoint: GET /clientes/contracts/{code}/stripe/summary
   * Note: This method does NOT fallback to admin endpoint
   */
  async getClientStripeBillingSummary(code: string): Promise<StripeBillingSummary> {
    const { data } = await api.get<StripeBillingSummary>(`/clientes/contracts/${code}/stripe/summary`);
    return data;
  },

  /**
   * Create Stripe billing portal session for a hiring code
   * Uses JWT authentication
   */
  async createStripeBillingPortalSession(code: string): Promise<StripeBillingPortalSession> {
    // Intentar endpoint de cliente primero (para usuarios autenticados como clientes)
    try {
      const { data } = await api.post<StripeBillingPortalSession>(`/clientes/contracts/${code}/stripe/portal`, {});
      return data;
    } catch {
      // Fallback a endpoint admin si el cliente no existe
      try {
        const { data } = await api.post<StripeBillingPortalSession>(`/crm/contracts/${code}/stripe/portal`, {});
        return data;
      } catch {
        throw new Error('No se pudo crear la sesión del portal de facturación');
      }
    }
  },

  /**
   * Create Stripe billing portal session for a hiring code (client endpoint only)
   * Uses JWT authentication - specifically for client portal
   * Backend endpoint: POST /clientes/contracts/{code}/stripe/portal
   * Note: This method does NOT fallback to admin endpoint
   */
  async createClientStripeBillingPortalSession(code: string): Promise<StripeBillingPortalSession> {
    const { data } = await api.post<StripeBillingPortalSession>(`/clientes/contracts/${code}/stripe/portal`, {});
    return data;
  },

  /**
   * Create a new contract (hiring code)
   * Uses /crm/contracts/ endpoint with JWT authentication
   */
  async createContract(request: ContractCreateRequest): Promise<Contract> {
    const body: any = {
      service_name: request.service_name,
      service_description: request.service_description,
      amount: request.amount != null ? request.amount : undefined, // En EUR
      currency: request.currency || 'EUR',
      payment_type: request.payment_type || 'one_time',
    };
    
    if (request.contract_template) {
      body.contract_template = request.contract_template;
    }
    if (request.grade) {
      body.grade = request.grade;
    }
    if (request.expires_in_days) {
      body.expires_in_days = request.expires_in_days;
    }
    if (request.client_name) {
      body.client_name = request.client_name;
    }
    if (request.client_email) {
      body.client_email = request.client_email;
    }
    if (request.client_passport) {
      body.client_passport = request.client_passport;
    }
    if (request.client_nie) {
      body.client_nie = request.client_nie;
    }
    if (request.client_address) {
      body.client_address = request.client_address;
    }
    if (request.client_city) {
      body.client_city = request.client_city;
    }
    if (request.client_province) {
      body.client_province = request.client_province;
    }
    if (request.client_postal_code) {
      body.client_postal_code = request.client_postal_code;
    }
    
    const { data } = await api.post<Contract>('/crm/contracts/', body);
    
    return normalizeHiringCode(data);
  },

  /**
   * Update contract
   * Uses PATCH /crm/contracts/{code} endpoint with JWT authentication
   */
  async updateContract(code: string, request: ContractUpdateRequest): Promise<Contract> {
    const body: any = {};
    
    // Solo incluir campos que están presentes en el request
    // Estado y configuración
    if (request.status !== undefined) body.status = request.status;
    if (request.payment_type !== undefined) body.payment_type = request.payment_type;
    if (request.grade !== undefined) body.grade = request.grade;
    if (request.currency !== undefined) body.currency = request.currency;
    if (request.amount !== undefined) body.amount = request.amount; // En EUR
    if (request.kyc_status !== undefined) body.kyc_status = request.kyc_status;
    
    // Cliente
    if (request.client_name !== undefined) body.client_name = request.client_name;
    if (request.client_email !== undefined) body.client_email = request.client_email;
    if (request.client_passport !== undefined) body.client_passport = request.client_passport;
    if (request.client_nie !== undefined) body.client_nie = request.client_nie;
    if (request.client_nationality !== undefined) body.client_nationality = request.client_nationality;
    if (request.client_address !== undefined) body.client_address = request.client_address;
    if (request.client_city !== undefined) body.client_city = request.client_city;
    if (request.client_province !== undefined) body.client_province = request.client_province;
    if (request.client_postal_code !== undefined) body.client_postal_code = request.client_postal_code;
    
    // Servicio
    if (request.service_name !== undefined) body.service_name = request.service_name;
    if (request.service_description !== undefined) body.service_description = request.service_description;
    
    // Pago manual
    if (request.manual_payment_confirmed !== undefined) body.manual_payment_confirmed = request.manual_payment_confirmed;
    if (request.manual_payment_method !== undefined) body.manual_payment_method = request.manual_payment_method;
    if (request.manual_payment_note !== undefined) body.manual_payment_note = request.manual_payment_note;
    
    // Suscripción / Stripe
    if (request.subscription_id !== undefined) body.subscription_id = request.subscription_id;
    if (request.subscription_status !== undefined) body.subscription_status = request.subscription_status;
    if (request.stripe_customer_id !== undefined) body.stripe_customer_id = request.stripe_customer_id;

    // Pago parcial
    if (request.first_payment_amount !== undefined) body.first_payment_amount = request.first_payment_amount; // En EUR
    
    // Expiración
    if (request.expires_in_days !== undefined) body.expires_in_days = request.expires_in_days;
    
    // Notas
    if (request.notes !== undefined) body.notes = request.notes;

    // Titulares (co-contratantes) — frontend usa "holders", backend "titulars"
    if (request.holders !== undefined) body.titulars = request.holders;

    // Dependientes / hijos
    if (request.dependents !== undefined) body.dependents = request.dependents;

    // Intentar primero con PATCH, si falla con 405, intentar con PUT
    try {
      const { data } = await api.patch<Contract>(`/crm/contracts/${code}`, body);
      return normalizeHiringCode(data);
    } catch (error: any) {
      // Si el error es 405 (Method Not Allowed), intentar con PUT
      if (error?.response?.status === 405) {
        console.warn('⚠️ PATCH no disponible, intentando con PUT...');
        const { data } = await api.put<Contract>(`/crm/contracts/${code}`, body);
        return normalizeHiringCode(data);
      }
      // Si es otro error, relanzarlo
      throw error;
    }
  },

  /**
   * Delete contract
   * Uses /crm/contracts/{code} DELETE endpoint with JWT authentication
   */
  async deleteContract(code: string): Promise<void> {
    await api.delete(`/crm/contracts/${code}`);
  },

  /**
   * Expire contract manually
   * Uses /crm/contracts/{code}/expire endpoint with JWT authentication
   */
  async expireContract(code: string): Promise<Contract> {
    const { data } = await api.post<Contract>(`/crm/contracts/${code}/expire`, {});
    return normalizeHiringCode(data);
  },

  /**
   * Expirar en bloque todos los contratos pending con fecha de expiración pasada.
   * POST /crm/contracts/bulk-expire-expired (JWT admin).
   * Devuelve cuántos contratos fueron expirados.
   */
  async bulkExpireExpired(): Promise<BulkExpireResponse> {
    const { data } = await api.post<BulkExpireResponse>('/crm/contracts/bulk-expire-expired', {});
    return data;
  },

  /**
   * Reenviar correo de bienvenida del contrato al cliente.
   * POST /crm/contracts/{code}/resend-contract-email (JWT admin).
   * Requiere PDF generado y email del cliente.
   */
  async resendContractWelcomeEmail(code: string): Promise<ResendContractEmailResponse> {
    const { data } = await api.post<ResendContractEmailResponse>(
      `/crm/contracts/${code}/resend-contract-email`,
      {}
    );
    return data;
  },

  /**
   * Download contract PDF
   */
  async downloadContract(code: string): Promise<Blob> {
    const { data } = await api.get<Blob>(`/hiring/${code}/contract/download`, {
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Download final contract PDF (endpoint público /hiring)
   */
  async downloadFinalContract(code: string): Promise<Blob> {
    const { data } = await api.get<Blob>(`/hiring/${code}/final-contract/download`, {
      responseType: 'blob',
      maxRedirects: 5,
    });
    return data;
  },

  /**
   * Descarga contrato definitivo vía CRM (JWT). 302 a Cloudinary o 404 si no disponible.
   * Usar en módulo contratos CRM. En 404 lanza con mensaje para mostrar en toast.
   */
  async downloadFinalContractCRM(code: string): Promise<Blob> {
    try {
      const { data } = await api.get<Blob>(`/crm/contracts/${code}/final-contract/download`, {
        responseType: 'blob',
        maxRedirects: 5,
      });
      return data;
    } catch (err: unknown) {
      const e = err as { response?: { status?: number }; message?: string };
      if (e?.response?.status === 404) {
        const msg = 'Contrato definitivo no disponible. Se genera después del pago confirmado.';
        throw Object.assign(new Error(msg), { response: e.response, status: 404 });
      }
      throw err;
    }
  },

  /**
   * Helper: Download contract and trigger browser download
   */
  async downloadContractFile(code: string, filename?: string, isFinal: boolean = false): Promise<void> {
    const blob = isFinal 
      ? await this.downloadFinalContract(code)
      : await this.downloadContract(code);
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `contrato-migro-${code}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Export contracts to CSV (client-side generation)
   */
  async exportContracts(filters?: ContractFilters): Promise<Blob> {
    // Get all contracts
    const response = await this.getContracts({
      ...filters,
      skip: 0,
      limit: 10000, // Get all
    });
    
    // Generate CSV
    const headers = [
      'Código',
      'Cliente',
      'Email',
      'Servicio',
      'Monto',
      'Estado',
      'KYC',
      'Grado',
      'Fecha Creación',
      'Fecha Expiración',
    ];
    
    const rows = response.items.map((contract) => [
      contract.hiring_code,
      contract.client_name,
      contract.client_email,
      contract.service_name,
      `${contract.amount} ${contract.currency}`,
      contract.status,
      contract.kyc_status || 'null',
      contract.grade || '',
      contract.created_at,
      contract.expires_at,
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    
    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  },

  /**
   * Get all annexes for a hiring code
   * Requires JWT authentication with admin role
   */
  async getAnnexes(hiringCode: string): Promise<ContractAnnex[]> {
    // El interceptor de axios agregará automáticamente el token JWT
    const { data } = await api.get<ContractAnnex[]>(`/crm/hiring/${hiringCode}/annexes`);
    return data;
  },

  /**
   * Upload attachment for annexes (photo, PDF)
   * POST /crm/hiring/{code}/annexes/attachments/upload (multipart/form-data, field: file)
   */
  async uploadAnnexAttachment(
    hiringCode: string,
    file: File
  ): Promise<AnnexAttachmentInfo> {
    const formData = new FormData();
    formData.append('file', file);
    const path = hiringCode
      ? `/crm/hiring/${hiringCode}/annexes/attachments/upload`
      : '/crm/hiring/annexes/attachments/upload';
    const { data } = await api.post<AnnexAttachmentInfo>(
      path,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return data;
  },

  /**
   * Create a new annex for a hiring code
   * Requires JWT authentication with admin role
   */
  async createAnnex(request: ContractAnnexCreateRequest): Promise<ContractAnnex> {
    const body: Record<string, unknown> = {
      hiring_code: request.hiring_code,
      title: request.title,
      content: request.content,
    };
    if (request.attachments?.length) {
      body.attachments = request.attachments;
    }
    const { data } = await api.post<ContractAnnex>(
      `/crm/hiring/${request.hiring_code}/annexes`,
      body
    );
    return data;
  },

  /**
   * Update an existing annex
   * Requires JWT authentication with admin role. annex_id: number (API) or string.
   */
  async updateAnnex(annexId: number | string, request: ContractAnnexUpdateRequest): Promise<ContractAnnex> {
    const id = typeof annexId === 'number' ? annexId : String(annexId);
    const { data } = await api.patch<ContractAnnex>(
      `/crm/hiring/annexes/${id}`,
      request
    );
    return data;
  },

  /**
   * Delete an annex
   * Requires JWT authentication with admin role. annex_id: number (API) or string.
   */
  async deleteAnnex(annexId: number | string): Promise<void> {
    const id = typeof annexId === 'number' ? annexId : String(annexId);
    await api.delete(`/crm/hiring/annexes/${id}`);
  },

  /**
   * Create contract via POST /crm/contracts/ (HiringPaymentCreate).
   * JWT admin. Incluye opcionalmente titulars, signers, dependents.
   * Respuesta: individual (Contract) o linked 2x1 (LinkedContractCreateResponse con codes[]).
   */
  async createContractV2(request: HiringPaymentCreate): Promise<AdminContractCreateResponse> {
    const body: Record<string, unknown> = {
      grade: request.grade,
      payment_type: request.payment_type || 'one_time',
      contract_template: request.contract_template || 'standard',
      expires_in_days: request.expires_in_days ?? 30,
      amount: request.amount ?? 400,
      currency: request.currency || 'EUR',
      client_name: request.client_name,
      client_email: request.client_email,
      client_passport: request.client_passport,
      client_nie: request.client_nie,
      client_nationality: request.client_nationality,
      client_address: request.client_address,
      client_city: request.client_city,
      client_province: request.client_province,
      client_postal_code: request.client_postal_code,
      service_name: request.service_name,
      service_description: request.service_description,
      manual_payment_note: request.manual_payment_note,
      manual_payment_method: request.manual_payment_method,
      manual_payment_confirmed: request.manual_payment_confirmed ?? false,
    };
    if (request.user_id) body.user_id = request.user_id;
    if (request.catalog_item_id != null) body.catalog_item_id = request.catalog_item_id;
    if (request.contract_type) body.contract_type = request.contract_type;
    if (request.holders?.length) body.holders = request.holders;
    if (request.titulars?.length) body.titulars = request.titulars;
    if (request.signers?.length) body.signers = request.signers;
    if (request.dependents?.length) body.dependents = request.dependents;
    if (request.linked_group_id) body.linked_group_id = request.linked_group_id;
    if (request.linked_second_participant) body.linked_second_participant = request.linked_second_participant;
    if (request.language) body.language = request.language;

    const { data } = await api.post<AdminContractCreateResponse>('/crm/contracts/', body);
    const raw = data as Record<string, unknown>;

    const nestedCandidates = [
      raw,
      (raw.data as Record<string, unknown> | undefined),
      (raw.result as Record<string, unknown> | undefined),
      (raw.payload as Record<string, unknown> | undefined),
    ].filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');

    const looksLikeLinkedResponse = nestedCandidates.some((candidate) => {
      const codes = candidate.codes;
      const contracts = candidate.contracts;
      return (
        (Array.isArray(codes) && codes.length > 0) ||
        (Array.isArray(contracts) && contracts.length > 0) ||
        typeof candidate.linked_group_id === 'string' ||
        typeof candidate.linkedGroupId === 'string'
      );
    });

    if (looksLikeLinkedResponse) {
      return data;
    }

    return normalizeHiringCode(data as Contract);
  },

  /**
   * Get contracts by linked_group_id
   * GET /crm/contracts/group/{linked_group_id}
   * Returns all contracts in a linked group (normally 2 for 2x1 offer)
   */
  async getContractsByGroup(linkedGroupId: string): Promise<{
    linked_group_id: string;
    contracts: Contract[];
  }> {
    const { data } = await api.get(`/crm/contracts/group/${linkedGroupId}`);
    return {
      linked_group_id: data.linked_group_id,
      contracts: data.contracts.map(normalizeHiringCode),
    };
  },

  /**
   * Obtiene las firmas manuscritas de un contrato desde workflow-progress
   */
  async getContractSignatures(code: string): Promise<{
    signers: Array<{
      id: string;
      name?: string;
      signed: boolean;
      signed_at?: string;
      signature_base64?: string;
    }>;
    accepted_terms: boolean;
    completed: boolean;
    completed_at?: string;
  } | null> {
    try {
      const { data } = await api.get(`/hiring/${code}/workflow-progress`);
      return data?.progress?.signatures ?? null;
    } catch {
      return null;
    }
  },

  // --- Installments (cuotas planificadas) ---

  async getInstallments(code: string) {
    const { data } = await api.get(`/crm/contracts/${code}/installments`);
    return data;
  },

  async markInstallmentPaid(
    code: string,
    number: number,
    body: { payment_method: string; note?: string; paid_at?: string },
  ) {
    const { data } = await api.patch(
      `/crm/contracts/${code}/installments/${number}/mark-paid`,
      body,
    );
    return data;
  },

  async updateInstallment(
    code: string,
    number: number,
    body: {
      due_date?: string;
      amount_cents?: number;
      note?: string;
      status?: string;
      payment_method?: string;
    },
  ) {
    const { data } = await api.patch(
      `/crm/contracts/${code}/installments/${number}`,
      body,
    );
    return data;
  },

  async regenerateInstallments(code: string) {
    const { data } = await api.post(`/crm/contracts/${code}/installments/generate`);
    return data;
  },

  async syncStripeInstallments(code: string) {
    const { data } = await api.post(`/crm/contracts/${code}/installments/sync-stripe`);
    return data;
  },

  async linkExpediente(code: string, expedienteId: string) {
    const { data } = await api.post(`/crm/contracts/${code}/link-expediente`, {
      expediente_id: expedienteId,
    });
    return data;
  },
};
